import { supabase } from '../lib/supabaseClient';
import { cachedFetch, invalidateCache } from './cache';
import type { Item, Supplier, Uom, UUID } from '../types/domain';

/**
 * Master data reads/writes. Reads go straight to PostgREST (`.from()`),
 * relying on RLS (migration 0004/0009) for tenant isolation — same pattern
 * every prior DEV_TESTED FASE's data access uses. Writes never INSERT
 * directly; they call the permission-gated RPC wrapper functions from
 * migration 0032 (`fn_create_master_item` / `fn_create_supplier`), so the
 * exact same server-side authorization gate a direct-SQL caller would hit is
 * what the browser hits too — there is no separate, weaker frontend write
 * path.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k. The RPC
 * functions themselves are DEV_TESTED (test13); this adapter's browser-side
 * call sites are not yet executed.
 */

/** uom is near-static reference data (rarely, if ever, changed after
 * initial tenant setup) and is re-fetched identically by nearly every
 * operational page — cached with a short TTL to cut that redundant
 * round-trip, not to avoid ever seeing a real change. */
export async function listUoms(): Promise<Uom[]> {
  return cachedFetch('uoms', async () => {
    const { data, error } = await supabase
      .from('uom')
      .select('uom_id, tenant_id, code, name, status')
      .eq('status', 'ACTIVE')
      .order('code');
    if (error) throw new Error(`listUoms failed: ${error.message}`);
    return (data ?? []) as Uom[];
  });
}

/** Cached (see listUoms doc) — items is read by nearly every operational
 * page purely to resolve item_id -> item_code/name for display, not
 * because that page is "about" items. */
export async function listItems(): Promise<Item[]> {
  return cachedFetch('items', async () => {
    const { data, error } = await supabase
      .from('items')
      .select('item_id, tenant_id, item_code, name, category_id, base_uom_id, is_stocked, status, created_at')
      .order('item_code');
    if (error) throw new Error(`listItems failed: ${error.message}`);
    return (data ?? []) as Item[];
  });
}

export interface CreateItemInput {
  tenantId: UUID;
  itemCode: string;
  name: string;
  baseUomId: UUID;
  categoryId?: UUID | null;
  isStocked?: boolean;
}

/** Calls fn_create_master_item (migration 0032, permission-gated on item.create). */
export async function createItem(input: CreateItemInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_master_item', {
    p_tenant_id: input.tenantId,
    p_item_code: input.itemCode,
    p_name: input.name,
    p_base_uom_id: input.baseUomId,
    p_category_id: input.categoryId ?? null,
    p_is_stocked: input.isStocked ?? true,
  });
  if (error) throw new Error(`fn_create_master_item failed: ${error.message}`);
  invalidateCache('items');
  return data as UUID;
}

/** Cached (see listUoms doc). */
export async function listSuppliers(): Promise<Supplier[]> {
  return cachedFetch('suppliers', async () => {
    const { data, error } = await supabase
      .from('suppliers')
      .select(
        'supplier_id, tenant_id, supplier_code, name, contact_name, contact_phone, contact_email, address, status, created_at'
      )
      .order('supplier_code');
    if (error) throw new Error(`listSuppliers failed: ${error.message}`);
    return (data ?? []) as Supplier[];
  });
}

export interface CreateSupplierInput {
  tenantId: UUID;
  supplierCode: string;
  name: string;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

/** Calls fn_create_supplier (migration 0032, permission-gated on supplier.create). */
export async function createSupplier(input: CreateSupplierInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_supplier', {
    p_tenant_id: input.tenantId,
    p_supplier_code: input.supplierCode,
    p_name: input.name,
    p_contact_name: input.contactName ?? null,
    p_contact_phone: input.contactPhone ?? null,
    p_contact_email: input.contactEmail ?? null,
  });
  if (error) throw new Error(`fn_create_supplier failed: ${error.message}`);
  invalidateCache('suppliers');
  return data as UUID;
}
